// PAEX occasion messaging (standalone PDP block, 2026-07-28) — the arrive-by
// badge (4 outcomes), the order-by boxes, and the get-it-by line, extracted
// from the availability block so merchants can move it and run several
// occasions side by side (per-instance targeting + dates).
//
// Engines are shared: destinations/templates from paex-store-core.js, dates
// from paex-edd.js. When the availability element is on the page (the normal
// PDP case) this element BRIDGES to it for online quantity, store stock,
// destination, and the postal editor — one source of truth, no duplicated
// data. Without it, only the neutral generic order-by box renders (never
// green on a guess).

if (!customElements.get('paex-occasion')) {
  customElements.define(
    'paex-occasion',
    class PaexOccasion extends HTMLElement {
      connectedCallback() {
        const dataNode = this.querySelector('[data-paex-occasion-data]');
        if (!dataNode) return;
        try {
          this.data = JSON.parse(dataNode.textContent);
        } catch (error) {
          return;
        }
        this.arriveBy = (this.dataset.arriveBy || '').slice(0, 10);
        if (!this.arriveBy) return;
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        this.render = this.render.bind(this);
        // the countdown and the cutoff flip need a clock; destination and
        // store changes re-evaluate the promise immediately
        this.tickTimer = setInterval(this.render, 30000);
        // geo/dest/store events all dispatch on DOCUMENT and do not bubble
        // to window — listen where they land (bugfix 2026-07-29: the badge
        // was blind to all of them and waited for the 30s tick)
        if (typeof PAEX_STORE_EVENT !== 'undefined') {
          document.addEventListener(PAEX_STORE_EVENT, this.render, { signal });
        }
        // C34 (round 2): on MOBILE an in-column order-by box waits (at most 3 s)
        // for the geo answer and the availability gate — the common outcome is
        // the pill, which rides on the image, so a box would flash in the
        // column and leave (two layout shifts). Desktop renders as before.
        this.mobileWait = window.matchMedia('(max-width: 749px)').matches;
        this.geoWait =
          this.mobileWait && !(typeof paexDeliveryReadGeo === 'function' && paexDeliveryReadGeo());
        if (this.mobileWait) {
          // after the availability element has applied the same geo event
          document.addEventListener('paex:geo-ready', () => { this.geoWait = false; setTimeout(this.render, 0); }, { signal });
          this.geoTimer = setTimeout(() => {
            this.mobileWait = false;
            this.geoWait = false;
            this.render();
          }, 3000);
        }
        document.addEventListener('paex:geo-ready', this.render, { signal });
        if (typeof PAEX_DEST_EVENT !== 'undefined') {
          document.addEventListener(PAEX_DEST_EVENT, this.render, { signal });
        }
        // Variant change (Sasha 2026-10-12): the pill reads the host's variantId
        // (onlineAvailable / inStockStores), so it must render AFTER the host
        // applied the variant — the host announces that (paex:pdp-avail, also
        // after its live-availability reconcile). Subscribing to the raw
        // variantChange ran FIRST whenever this block sits above the
        // availability block (subscription order = block order, e.g.
        // templates/product.pdp-size-charts.json) and left the pill one variant
        // behind: green on the sold-out size, "In store only" on the in-stock
        // one. Section-filtered: the quick-view drawer clones this element with
        // a rewritten data-section and must not follow the PDP's changes.
        document.addEventListener('paex:pdp-avail', (event) => {
          if (event.detail && event.detail.sectionId === this.dataset.section) this.render();
        }, { signal });
        // this block can sit ABOVE the availability block (merchant's order):
        // its script then runs before paex-delivery-method defines — re-render
        // the moment the host class registers, not at the next tick
        if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined('paex-delivery-method').then(() => this.render()).catch(() => {});
        }
        // Halloween clock - PE (2026-10-09): with the clock config on the page
        // (clock active, snippets/paex-clock-config.liquid) the badge waits for
        // assets/paex-clock.js (end of body) and follows its rules — no
        // pre-clock badge that flips a moment later. 4 s safety: a clock that
        // never starts leaves the pre-clock badge in place.
        this.clockWait = Boolean(document.getElementById('PaexClockConfig')) && !window.paexClock;
        if (window.paexClock) this.startClock();
        else if (this.clockWait) {
          document.addEventListener('paex:clock-ready', () => this.startClock(), { once: true, signal });
          this.clockWaitTimer = setTimeout(() => {
            this.clockWait = false;
            this.render();
          }, 4000);
        }
        this.render();
      }

      /* ---------------- Halloween clock - PE (2026-10-09) ---------------- */

      // One clock, one rule: the pill takes the PDP block's product verdict
      // (paex-delivery-method.js refreshClockVerdict — the clock's destination
      // and rules: remote / US / unknown never get green), its last-day
      // "Order in {time}" counts with paexClock.bindCountdown (one shared,
      // second-aligned ticker), and the 30 s interval goes away. While the
      // pill ticks, [data-paex-ticking] tells the ship tile and the sticky
      // line to show their date wording instead (one countdown per page).
      startClock() {
        // once per connection: a re-connected element gets a fresh
        // abortController (connectedCallback), so its listeners re-arm here
        const signal = this.abortController.signal;
        if (!window.paexClock || this.clockSignal === signal) return;
        this.clockSignal = signal;
        this.clock = window.paexClock;
        this.clockWait = false;
        clearTimeout(this.clockWaitTimer);
        ['paex:clock-change', 'paex:clock-cutoff', 'paex:pdp-clock'].forEach((type) => {
          document.addEventListener(type, this.render, { signal });
        });
        this.render();
      }

      clockMode() {
        return Boolean(this.clock && this.clock.state && this.clock.state.phase !== 'off');
      }

      setTicking(on) {
        if (this.hasAttribute('data-paex-ticking') === on) return;
        this.toggleAttribute('data-paex-ticking', on);
        document.dispatchEvent(new CustomEvent('paex:occ-ticking', { detail: { ticking: on } }));
      }

      // the {time} node + a visually-hidden minute-level reading (digits are
      // aria-hidden); a re-render on expiry drops the pill to its date wording
      bindPillTime(root) {
        const el = root && root.querySelector('.paex-occ__time');
        if (!el) return false;
        let sr = el.nextElementSibling;
        if (!sr || !sr.classList.contains('visually-hidden')) {
          sr = document.createElement('span');
          sr.className = 'visually-hidden';
          el.after(sr);
        }
        return this.clock.bindCountdown(el, {
          srEl: sr,
          // editor preview counts to the next daily cutoff (see renderClock)
          daily: this.hasAttribute('data-preview-countdown'),
          onExpire: () => {
            const slot = this.querySelector('[data-occasion]');
            if (slot) delete slot.dataset.text;
            if (this.overlay) delete this.overlay.dataset.key;
            this.render();
          },
        });
      }

      renderClock(slot) {
        const texts = this.data.texts;
        const host = this.host();
        // M7: sellability (the ATC gate), not the nightly mirror count
        const available = host && typeof host.onlineAvailable === 'function'
          ? host.onlineAvailable()
          : this.dataset.available === '1';
        const region = host ? host.destRegion() : null;
        const place = host ? host.destPlace() : '';
        const inFootprint = Boolean(host && host.inFootprint);
        // the block's product verdict; pending (async) = keep the slot as is
        let verdict = this.clock.state.verdict;
        let verdictEstimate = this.clock.state.estimate;
        if (host) {
          if (host.clockVerdict === undefined) return;
          verdict = host.clockVerdict;
          verdictEstimate = host.clockEstimate;
        }
        // theme editor only (block setting "Preview the last-day countdown",
        // Sasha 2026-10-09): show the ticking badge as it looks on the last
        // order day, counting to the next daily cutoff; never on the storefront
        const previewCountdown = this.hasAttribute('data-preview-countdown');
        if (previewCountdown && available) verdict = 'lastday';
        // honesty: the order-by box's date comes from the transit TABLE, so it
        // only stands beside a verdict whose estimate is the table's own (no
        // live upgrade, or a live latest date equal to the table date)
        const tableVerdict = Boolean(verdictEstimate && verdictEstimate.base &&
          (!verdictEstimate.live || verdictEstimate.max === verdictEstimate.base.arrivalKey));
        const arriveBy = (this.clock.config.occasion && this.clock.config.occasion.arriveBy) || this.arriveBy;
        let tone = '';
        let text = '';
        let container = 'pill';
        let boxFilled = false;
        let countdown = false;

        if (!available) {
          const best = host && host.pickupEnabled && inFootprint ? host.inStockStores(null)[0] : null;
          if (best && texts.badgeStoreOnly) {
            tone = 'amber';
            text = paexDeliveryFormat(texts.badgeStoreOnly, { occasion: texts.occasionName });
          }
        } else if (verdict === 'late') {
          if (texts.badgeWont) {
            tone = 'amber';
            text = paexDeliveryFormat(texts.badgeWont, { occasion: texts.occasionName });
          }
        } else if (verdict === 'ontime' || verdict === 'lastday') {
          tone = 'green';
          countdown = verdict === 'lastday' && Boolean(texts.badgeCountdown) &&
            (previewCountdown ? this.clock.msToDailyCutoff() : this.clock.msToCutoff()) >= 1000;
          text = countdown ? 'cd:' + texts.badgeCountdown : texts.badgeArrives;
          const orderBy = region ? paexEddOrderBy(region, this.data.edd, arriveBy) : null;
          if (!inFootprint && orderBy && texts.orderByKnown && !tableVerdict) {
            // live date differs from the table: the plain pill, no table date
            countdown = false;
            text = texts.badgeArrives;
          } else if (!inFootprint && orderBy && texts.orderByKnown) {
            tone = 'amber';
            container = 'box';
            countdown = false;
            const boxKey = 'k:' + orderBy.key + ':' + (place || '');
            if (slot.dataset.boxKey !== boxKey) {
              slot.dataset.boxKey = boxKey;
              paexFillTemplate(slot, texts.orderByKnown, {
                date: paexEddFormatDate(orderBy.orderBy),
                place: place || '',
              }, { date: 'paex-occ__obdate' });
            }
            boxFilled = true;
            text = 'box';
          }
        }
        if (!region && available && texts.orderByGeneric && !this.geoWait) {
          // no destination yet: the same neutral "typical" box as before
          const generic = paexEddOrderBy('*', this.data.edd, arriveBy);
          if (generic) {
            tone = 'amber';
            container = 'boxneutral';
            countdown = false;
            const boxKey = 'g:' + generic.key;
            if (slot.dataset.boxKey !== boxKey) {
              slot.dataset.boxKey = boxKey;
              paexFillTemplate(slot, texts.orderByGeneric, {
                date: paexEddFormatDate(generic.orderBy),
              }, { date: 'paex-occ__obdate' });
              if (texts.getYourDate && host && typeof host.openPostalEditor === 'function') {
                slot.appendChild(document.createTextNode(' · '));
                const opener = document.createElement('button');
                opener.type = 'button';
                opener.className = 'paex-occ__getdate';
                opener.textContent = texts.getYourDate;
                opener.addEventListener('click', () => this.host()?.openPostalEditor(), {
                  signal: this.abortController.signal,
                });
                slot.appendChild(opener);
              }
            }
            boxFilled = true;
            text = 'box';
          }
        }

        // C34: mobile, gate not settled yet → no in-column box for now
        if (text === 'box' && this.mobileWait && host && host.pickupEnabled && !host.gateResolved) {
          text = '';
        }
        let ticking = false;
        slot.hidden = text === '';
        if (slot.hidden) {
          slot.textContent = '';
          delete slot.dataset.tone;
          delete slot.dataset.container;
          delete slot.dataset.text;
          delete slot.dataset.boxKey;
        } else {
          slot.dataset.tone = tone;
          slot.dataset.container = container;
          if (boxFilled) {
            delete slot.dataset.text;
          } else if (countdown) {
            ticking = slot.dataset.text === text && Boolean(slot.querySelector('.paex-occ__time.paex-clock-cd'));
            if (!ticking) {
              slot.dataset.text = text;
              delete slot.dataset.boxKey;
              paexFillTemplate(slot, texts.badgeCountdown, {
                time: '',
                occasion: texts.occasionName,
              }, { time: 'paex-occ__time' });
              if (typeof PAEX_CLOCK_SVG !== 'undefined') slot.insertAdjacentHTML('afterbegin', PAEX_CLOCK_SVG);
              ticking = this.bindPillTime(slot);
              if (!ticking) {
                // no honest countdown (cutoff reached, untrusted device clock)
                slot.dataset.text = texts.badgeArrives;
                slot.textContent = texts.badgeArrives;
              }
            }
          } else if (slot.dataset.text !== text) {
            slot.dataset.text = text;
            delete slot.dataset.boxKey;
            slot.textContent = text; // text-only badge (Sasha 2026-07-28)
          }
        }
        this.syncOverlay(slot);
        this.setTicking(ticking);
      }

      disconnectedCallback() {
        clearInterval(this.tickTimer);
        clearTimeout(this.geoTimer);
        clearTimeout(this.clockWaitTimer);
        if (this.overlay) {
          this.overlay.remove();
          this.overlay = null;
        }
        if (this.abortController) this.abortController.abort();
      }

      // the availability element is the single source of stock + destination
      // truth when present — but only once its class has UPGRADED it (before
      // that, calling its methods throws and kills the render)
      host() {
        const el = document.querySelector('paex-delivery-method');
        return el && typeof el.estimate === 'function' ? el : null;
      }

      onlineQty() {
        const host = this.host();
        if (host && typeof host.onlineQty === 'function') return host.onlineQty();
        return this.dataset.available === '1' ? 1 : 0;
      }

      todayKey() {
        const now = paexEddWarehouseNow(this.data.edd.tz);
        return paexEddKey(now.y, now.m, now.d);
      }

      render() {
        const slot = this.querySelector('[data-occasion]');
        if (!slot || typeof paexEddOrderBy !== 'function') return;
        // Halloween clock - PE (2026-10-09): clock rules while it is active
        if (this.clockWait) return;
        if (this.clockMode()) {
          // the clock's events + shared ticker replace the 30 s interval
          if (this.tickTimer) {
            clearInterval(this.tickTimer);
            this.tickTimer = null;
          }
          this.renderClock(slot);
          return;
        }
        // clock gone quiet (phase off, e.g. past the occasion at midnight):
        // the pre-clock 30 s refresh comes back with the pre-clock rules
        if (this.clock && !this.tickTimer && this.isConnected) this.tickTimer = setInterval(this.render, 30000);
        const texts = this.data.texts;
        const host = this.host();
        const online = this.onlineQty();
        const region = host ? host.destRegion() : null;
        const estimate = host ? host.estimate() : null;
        const place = host ? host.destPlace() : '';
        const inFootprint = Boolean(host && host.inFootprint);
        const arriveBy = this.arriveBy;
        let tone = '';
        let text = '';
        let container = 'pill';
        let boxFilled = false;

        if (online <= 0) {
          const best = host && host.pickupEnabled && inFootprint ? host.inStockStores(null)[0] : null;
          if (best && texts.badgeStoreOnly) {
            tone = 'amber';
            text = paexDeliveryFormat(texts.badgeStoreOnly, { occasion: texts.occasionName });
          }
        } else if (region && estimate) {
          const orderBy = paexEddOrderBy(region, this.data.edd, arriveBy);
          if (estimate.arrivalKey <= arriveBy) {
            // beforeCutoff, not shipsToday: the order-by deadline is the cutoff
            // even when processing days delay the actual dispatch (2026-09-21)
            const today = estimate.beforeCutoff && orderBy && orderBy.key === this.todayKey();
            tone = 'green';
            text = today && estimate.msToCutoff > 0 && texts.badgeCountdown
              ? paexDeliveryFormat(texts.badgeCountdown, {
                  time: paexEddFormatCountdown(estimate.msToCutoff),
                  occasion: texts.occasionName,
                })
              : texts.badgeArrives;
          } else if (texts.badgeWont) {
            tone = 'amber';
            text = paexDeliveryFormat(texts.badgeWont, { occasion: texts.occasionName });
          }
          if (!inFootprint && orderBy && estimate.arrivalKey <= arriveBy && texts.orderByKnown) {
            tone = 'amber';
            container = 'box';
            const boxKey = 'k:' + orderBy.key + ':' + (place || '');
            if (slot.dataset.boxKey !== boxKey) {
              slot.dataset.boxKey = boxKey;
              paexFillTemplate(slot, texts.orderByKnown, {
                date: paexEddFormatDate(orderBy.orderBy),
                place: place || '',
              }, { date: 'paex-occ__obdate' });
            }
            boxFilled = true;
            text = 'box';
          }
        }
        if (!region && online > 0 && texts.orderByGeneric && !this.geoWait) {
          // no destination yet: generic date for most of Canada in a NEUTRAL
          // box — a typical figure that says so, plus a way to get the real one
          const generic = paexEddOrderBy('*', this.data.edd, arriveBy);
          if (generic) {
            tone = 'amber';
            container = 'boxneutral';
            const boxKey = 'g:' + generic.key;
            if (slot.dataset.boxKey !== boxKey) {
              slot.dataset.boxKey = boxKey;
              paexFillTemplate(slot, texts.orderByGeneric, {
                date: paexEddFormatDate(generic.orderBy),
              }, { date: 'paex-occ__obdate' });
              if (texts.getYourDate && host && typeof host.openPostalEditor === 'function') {
                slot.appendChild(document.createTextNode(' · '));
                const opener = document.createElement('button');
                opener.type = 'button';
                opener.className = 'paex-occ__getdate';
                opener.textContent = texts.getYourDate;
                opener.addEventListener('click', () => this.host()?.openPostalEditor(), {
                  signal: this.abortController.signal,
                });
                slot.appendChild(opener);
              }
            }
            boxFilled = true;
            text = 'box';
          }
        }
        // Unknown destination otherwise: no badge at all. Never green on a
        // guess — the availability foot invites the postal code.

        // C34: mobile, gate not settled yet → no in-column box for now
        if (text === 'box' && this.mobileWait && host && host.pickupEnabled && !host.gateResolved) {
          text = '';
        }
        slot.hidden = text === '';
        if (slot.hidden) {
          slot.textContent = '';
          delete slot.dataset.tone;
          delete slot.dataset.container;
          delete slot.dataset.text;
          delete slot.dataset.boxKey; // a re-shown box must refill
        } else {
          slot.dataset.tone = tone;
          slot.dataset.container = container;
          if (boxFilled) {
            delete slot.dataset.text;
          } else if (slot.dataset.text !== text) {
            slot.dataset.text = text;
            slot.textContent = text;
            // the green badge is TEXT-ONLY (Sasha 2026-07-28); only the
            // last-day countdown variant carries the clock
            if (tone === 'green' && typeof PAEX_CLOCK_SVG !== 'undefined') {
              const isCountdown = text.indexOf(':') !== -1 || /\d+\s*h/.test(text);
              if (isCountdown) slot.insertAdjacentHTML('afterbegin', PAEX_CLOCK_SVG);
            }
          }
        }
        this.syncOverlay(slot);
      }

      // C34 (PDP restyle round 2, 2026-10-05): on mobile the PILL rides on the
      // main product image (bottom-left, above the thumbnails) instead of above
      // the title. The overlay mirrors the slot into the gallery's main-image
      // viewport (.slider-container — the box the wishlist heart already uses:
      // stable while the slides translate). paex-occasion.css shows it ≤749px
      // only and hides the in-column pill there; desktop is unchanged. Order-by
      // boxes never move. Absolutely positioned → no layout shift.
      syncOverlay(slot) {
        const pill = !slot.hidden && slot.dataset.container === 'pill';
        if (!this.overlay) {
          if (!pill) return;
          const root = document.getElementById('MainProduct-' + this.dataset.section);
          const viewport = root && root.querySelector('.product-media-gallery .media-gallery > .product-media-slider > .slider-container');
          if (!viewport) return;
          this.overlay = document.createElement('div');
          this.overlay.className = 'paex-occ__slot';
          this.overlay.dataset.occOverlay = '';
          viewport.appendChild(this.overlay);
        }
        this.overlay.hidden = !pill;
        if (!pill) return;
        if (this.overlay.dataset.tone !== slot.dataset.tone) this.overlay.dataset.tone = slot.dataset.tone;
        this.overlay.dataset.container = 'pill';
        if (this.clockMode()) {
          // Halloween clock - PE (2026-10-09): copy only when the pill's
          // wording changes (never per second — the ticker writes each copy's
          // own text node), then bind the overlay's own {time} node: on mobile
          // the overlay is the visible pill
          const key = (slot.dataset.text || '') + '|' + (slot.dataset.tone || '');
          if (this.overlay.dataset.key === key) return;
          this.overlay.dataset.key = key;
          this.overlay.replaceChildren(...Array.from(slot.childNodes, (node) => node.cloneNode(true)));
          if (this.overlay.querySelector('.paex-occ__time')) this.bindPillTime(this.overlay);
          return;
        }
        if (this.overlay.innerHTML !== slot.innerHTML) this.overlay.innerHTML = slot.innerHTML;
      }

      // (the get-it-by line was removed 2026-07-28 — the badge is a label,
      // the date already lives in the ship tile)
    }
  );
}
